import { FAMILY_BY_ID } from '../data/families';
import { SHOP } from '../data/shop';
import type { Difficulty, Lane, ShopBranchId, Side } from '../data/types';
import type { Match, TowerState } from './match';

interface AiProfile {
  decisionInterval: number;
  mergeChance: number;
  shopChance: number;
  laneInterval: number;
  laneChance: number;
  commanderChance: number;
  reserve: number;
}

const PROFILES: Record<Difficulty, AiProfile> = {
  recruit: {
    decisionInterval: 2.4,
    mergeChance: 0.35,
    shopChance: 0.3,
    laneInterval: 6,
    laneChance: 0.75,
    commanderChance: 0.5,
    reserve: 40,
  },
  veteran: {
    decisionInterval: 0.9,
    mergeChance: 0.8,
    shopChance: 0.6,
    laneInterval: 3,
    laneChance: 1,
    commanderChance: 0.9,
    reserve: 20,
  },
  warlord: {
    decisionInterval: 0.5,
    mergeChance: 1,
    shopChance: 0.8,
    laneInterval: 2,
    laneChance: 1,
    commanderChance: 1,
    reserve: 0,
  },
};

/**
 * Heuristic opponent that only acts through the match's public command
 * surface, so it is subject to exactly the same rules as the human player.
 * All randomness comes from the match RNG for deterministic replays.
 */
export class AiController {
  private readonly profile: AiProfile;
  private decisionTimer = 1.5;
  private laneTimer = 8;
  private focusLane: Lane = 1;
  /** Until this match time the AI plays slowly and keeps a small board (tutorial grace). */
  calmUntil = 0;

  private get calm(): boolean {
    return this.match.time < this.calmUntil;
  }

  constructor(
    private readonly match: Match,
    private readonly side: Side,
    difficulty: Difficulty,
  ) {
    this.profile = PROFILES[difficulty];
    this.focusLane = match.rng.chance(0.5) ? 0 : 1;
    // Stagger timers so two AIs never make decisions on the same tick.
    this.decisionTimer = match.rng.range(1.2, 2.4);
    this.laneTimer = match.rng.range(6, 10);
  }

  update(dt: number): void {
    if (this.match.ended) return;
    this.decisionTimer -= dt;
    this.laneTimer -= dt;
    if (this.decisionTimer <= 0) {
      this.decisionTimer = this.profile.decisionInterval * (this.calm ? 3 : 1) * this.match.rng.range(0.7, 1.3);
      this.decide();
    }
    if (this.laneTimer <= 0) {
      this.laneTimer = this.profile.laneInterval * this.match.rng.range(0.7, 1.3);
      if (!this.calm) this.manageLanes();
    }
  }

  private decide(): void {
    const m = this.match;
    const rng = m.rng;
    const p = m.players[this.side];
    const towers = m.towersOf(this.side);

    // 1. Merge.
    const pairs = m.mergePairs(this.side);
    if (pairs.length > 0 && rng.chance(this.profile.mergeChance)) {
      pairs.sort((a, b) => b[2] - a[2]);
      const [ta, tb] = pairs[0];
      const dest = this.betterTile(ta, tb);
      m.drop(this.side, dest === ta ? tb : ta, dest);
      return;
    }

    // 2. Commander.
    if (p.commanderCooldown <= 0 && rng.chance(this.profile.commanderChance)) {
      if (this.shouldUseCommander()) {
        m.useCommander(this.side);
        return;
      }
    }

    // 3. Summon.
    const cost = m.summonCost(this.side);
    const empties = m.emptyTiles(this.side);
    let wantSummon = towers.length < 4 || p.essence - cost >= this.profile.reserve;
    if (this.calm && towers.length >= 3) wantSummon = false;
    if (empties.length > 0 && p.essence >= cost && wantSummon && rng.chance(0.9)) {
      m.summon(this.side);
      return;
    }

    // 4. Shop.
    if (rng.chance(this.profile.shopChance)) {
      const branch = this.pickShopBranch();
      if (branch) {
        const c = m.shopCost(this.side, branch);
        const fullBoard = empties.length === 0 && pairs.length === 0;
        if (c !== null && p.essence >= c + (fullBoard ? 0 : cost * 0.5)) m.buyUpgrade(this.side, branch);
      }
    }
  }

  private betterTile(a: number, b: number): number {
    const rowA = Math.floor(a / 5);
    const rowB = Math.floor(b / 5);
    const fam = FAMILY_BY_ID[this.match.players[this.side].towers[a]!.familyId];
    if (fam.tower.attack === 'support') {
      // Support wants many shooting neighbours.
      return this.shootingNeighbours(a) >= this.shootingNeighbours(b) ? a : b;
    }
    if (rowA !== rowB) return rowA < rowB ? a : b;
    const ta = this.match.players[this.side].towers[a]!;
    return ta.lane === this.focusLane ? a : b;
  }

  private shootingNeighbours(tile: number): number {
    const towers = this.match.players[this.side].towers;
    const col = tile % 5;
    const row = Math.floor(tile / 5);
    const n = [col > 0 ? tile - 1 : -1, col < 4 ? tile + 1 : -1, row > 0 ? tile - 5 : -1, row < 2 ? tile + 5 : -1];
    let count = 0;
    for (const i of n) if (i >= 0 && towers[i] && FAMILY_BY_ID[towers[i]!.familyId].tower.attack !== 'support') count++;
    return count;
  }

  private shouldUseCommander(): boolean {
    const m = this.match;
    const p = m.players[this.side];
    const pressure = m.lanePressure(this.side);
    const maxPressure = Math.max(pressure[0], pressure[1]);
    switch (p.commanderId) {
      case 'vael':
        return maxPressure > 320 || (m.time > 60 && m.towersOf(this.side).length >= 4 && m.rng.chance(0.3));
      case 'oda':
        return m.mergePairs(this.side).length === 0 && m.towersOf(this.side).length >= 2 && m.emptyTiles(this.side).length > 0;
      case 'brakk': {
        const counts = m.laneCounts(this.side);
        return maxPressure > 250 || counts[0] + counts[1] >= 6;
      }
      default:
        return false;
    }
  }

  private pickShopBranch(): ShopBranchId | null {
    const m = this.match;
    const p = m.players[this.side];
    const pressure = m.lanePressure(this.side);
    const underPressure = Math.max(pressure[0], pressure[1]) > 200;
    const weights: Record<ShopBranchId, number> = {
      barrage: underPressure ? 3 : 1.5,
      tempo: 2,
      muster: underPressure ? 1 : 2.5,
      greed: m.time < 150 ? 3 : 0.8,
    };
    let total = 0;
    const entries: Array<[ShopBranchId, number]> = [];
    for (const s of SHOP) {
      if (p.shop[s.id] >= s.costs.length) continue;
      entries.push([s.id, weights[s.id]]);
      total += weights[s.id];
    }
    if (entries.length === 0) return null;
    let r = m.rng.next() * total;
    for (const [id, w] of entries) {
      r -= w;
      if (r <= 0) return id;
    }
    return entries[entries.length - 1][0];
  }

  private manageLanes(): void {
    const m = this.match;
    if (!m.rng.chance(this.profile.laneChance)) return;
    const towers = m.towersOf(this.side);
    if (towers.length < 2) return;

    const pressure = m.lanePressure(this.side);
    const enemyCounts = m.laneCounts(this.side === 0 ? 1 : 0);
    const hot: Lane = pressure[0] >= pressure[1] ? 0 : 1;
    const hotRatio = pressure[hot] / Math.max(1, pressure[1 - hot]);
    const underPressure = pressure[hot] > 220 && hotRatio > 1.8;
    const weak: Lane = enemyCounts[0] <= enemyCounts[1] ? 0 : 1;
    this.focusLane = underPressure ? hot : weak;

    const n = towers.length;
    const half = Math.floor(n / 2);
    let target: [number, number] = [half, n - half];
    if (n % 2 === 1) target = this.focusLane === 0 ? [half + 1, half] : [half, half + 1];
    else if (underPressure) target = hot === 0 ? [half + 1, half - 1] : [half - 1, half + 1];

    if (m.board === 'painted') {
      this.balancePainted(towers, target);
      return;
    }
    this.applySplit(towers, target);
  }

  /** Move towers until the two lanes match the plan. One pass, free in arrow mode. */
  private applySplit(towers: TowerState[], target: [number, number]): void {
    const m = this.match;
    const counts: [number, number] = [0, 0];
    for (const t of towers) counts[t.lane]++;
    for (const lane of [0, 1] as Lane[]) {
      let need = target[lane] - counts[lane];
      if (need <= 0) continue;
      const donors = towers.filter((t) => t.lane !== lane).sort((a, b) => this.laneAffinity(b, lane) - this.laneAffinity(a, lane));
      for (const t of donors) {
        if (need <= 0) break;
        const from = t.lane;
        if (m.setLane(this.side, t.tile, lane)) {
          counts[from]--;
          counts[lane]++;
          need--;
        }
      }
    }
  }

  /** Walk one tower onto an empty cell of the lane that is short, when the player can afford it. */
  private balancePainted(towers: TowerState[], target: [number, number]): void {
    const m = this.match;
    const p = m.players[this.side];
    if (p.essence < 15) return;
    const counts: [number, number] = [0, 0];
    for (const t of towers) counts[t.lane]++;
    const lane: Lane | null = counts[0] < target[0] ? 0 : counts[1] < target[1] ? 1 : null;
    if (lane === null) return;
    const wrong = towers.filter((t) => t.lane !== lane);
    const empty = m.emptyTiles(this.side).filter((tile) => m.cellLanes[tile] === lane);
    if (wrong.length === 0 || empty.length === 0) return;
    m.drop(this.side, wrong[0].tile, empty[0]);
  }

  private laneAffinity(t: TowerState, lane: Lane): number {
    const col = t.tile % 5;
    return lane === 0 ? 4 - col : col;
  }
}
